/* Presentation enhancements only: Dawn still selects variants, prices and cart lines. */
(() => {
  'use strict';
  // CSS handles the pin and its section boundary. Only tall columns need a
  // directional offset so their bottom stays reachable, as in the reference.
  class ProductInfoSticky {
    constructor(root) {
      this.info = root.querySelector('.product__info-container');
      if (!this.info) return;
      this.details = root.querySelector('.sp-pdp-details');
      this.gallery = root.querySelector('.product__media-wrapper');
      this.desktop = matchMedia('(min-width:1008px)');
      this.controller = new AbortController();
      this.lastY = window.scrollY;
      this.top = 0;
      this.frame = null;
      this.measure = () => {
        const infoHeight = this.info.getBoundingClientRect().height;
        // The reference reserves the part of the information column extending
        // below the gallery, even when every tab is closed. Let the existing
        // detail margins supply the sticky travel instead of collapsing the
        // containing block to the information height.
        if (this.details && this.gallery && this.desktop.matches) {
          const minimum = Math.max(0, infoHeight - this.gallery.getBoundingClientRect().height);
          this.details.style.setProperty('--sp-details-min-height', `${minimum}px`);
        } else {
          this.details?.style.removeProperty('--sp-details-min-height');
        }
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sp-header-height')) || 0;
        this.maxTop = header + 25;
        this.minTop = Math.min(this.maxTop, innerHeight - infoHeight);
        this.top = Math.max(this.minTop, Math.min(this.top, this.maxTop));
        if (this.minTop === this.maxTop) this.top = this.maxTop;
        this.sync();
      };
      this.tick = () => {
        this.frame = null;
        if (!this.active || !this.desktop.matches) return;
        const y = window.scrollY;
        if (y !== this.lastY) {
          this.top = Math.min(this.maxTop, Math.max(this.minTop, this.top + this.lastY - y));
          this.lastY = y;
          this.info.style.setProperty('--sp-info-sticky-top', `${this.top}px`);
        }
        // No scroll listener or repeated layout measurements. Suspend offscreen.
        this.frame = requestAnimationFrame(this.tick);
      };
      this.sync = () => {
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.lastY = window.scrollY;
        if (!this.desktop.matches) {
          this.info.style.removeProperty('--sp-info-sticky-top');
          return;
        }
        this.info.style.setProperty('--sp-info-sticky-top', `${this.top}px`);
        if (this.active && this.minTop < this.maxTop) this.frame = requestAnimationFrame(this.tick);
      };
      this.resize = new ResizeObserver(this.measure);
      this.resize.observe(this.info);
      if (this.gallery) this.resize.observe(this.gallery);
      const header = document.querySelector('sp-header');
      if (header) this.resize.observe(header);
      this.visibility = new IntersectionObserver(entries => {
        this.active = entries[0].isIntersecting;
        this.sync();
      });
      this.visibility.observe(root.querySelector('.product'));
      this.desktop.addEventListener('change', this.measure, {signal:this.controller.signal});
      window.addEventListener('resize', this.measure, {signal:this.controller.signal});
      this.measure();
    }
    destroy() {
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.info?.style.removeProperty('--sp-info-sticky-top');
      this.details?.style.removeProperty('--sp-details-min-height');
    }
  }
  if (!customElements.get('sp-size-chart')) customElements.define('sp-size-chart', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const toggle = this.querySelector('[role=switch]');
      toggle.addEventListener('click', () => {
        const inches = toggle.getAttribute('aria-checked') !== 'true';
        toggle.setAttribute('aria-checked', String(inches));
        this.querySelector('[data-size-toggle-label]').textContent = inches ? 'IN' : 'CM';
        this.querySelector('[data-size-unit]').textContent = inches ? 'Inches' : 'CM';
        this.querySelectorAll('[data-size-cm]').forEach(cell => {
          cell.textContent = inches ? cell.dataset.sizeCm.replace(/\d+(?:\.\d+)?/g, value => String(Math.round(Number(value) / 2.54 * 10) / 10)) : cell.dataset.sizeCm;
        });
      }, {signal:this.controller.signal});
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
  if (!customElements.get('sp-product-presentation')) customElements.define('sp-product-presentation', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      this.sticky = new ProductInfoSticky(this.root);
      const update = () => {
        if (this.root.querySelector('sp-promo-price-test')?.ready) return;
        const source = this.root.querySelector('[data-pdp-button-price]');
        const target = this.root.querySelector('[data-pdp-submit-price]');
        if (source && target && target.textContent !== source.textContent) target.textContent = source.textContent;
      };
      this.observer = new MutationObserver(update);
      this.observer.observe(this.root, { childList:true, subtree:true });
      update();
    }
    disconnectedCallback() { this.observer?.disconnect(); this.sticky?.destroy(); }
  });

  if (!customElements.get('sp-product-gallery-controls')) customElements.define('sp-product-gallery-controls', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.gallery = this.closest('media-gallery');
      this.list = this.gallery.querySelector('.product__media-list');
      const move = (direction) => {
        const items = [...this.list.children].filter(item => item.matches('[data-media-id]') && !item.hidden);
        if (!items.length) return;
        const center = this.list.scrollLeft + this.list.clientWidth / 2;
        const current = items.findIndex(item => item.offsetLeft <= center && item.offsetLeft + item.offsetWidth > center);
        const next = items[(Math.max(0, current) + direction + items.length) % items.length];
        this.gallery.setActiveMedia(next.dataset.mediaId, false);
      };
      this.querySelector('[data-pdp-previous]').addEventListener('click', () => move(-1), {signal});
      this.querySelector('[data-pdp-next]').addEventListener('click', () => move(1), {signal});
      this.list.addEventListener('keydown', event => {
        if (event.target.closest('video,input,select,model-viewer')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1); }
      }, {signal});
      this.list.addEventListener('dragstart', event => { if (event.target.matches('img')) event.preventDefault(); }, {signal});
      this.list.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,video,iframe,model-viewer')) return;
        this.start = {x:event.clientX,y:event.clientY};
      }, {signal});
      window.addEventListener('pointerup', event => {
        if (!this.start) return;
        const dx = event.clientX - this.start.x, dy = event.clientY - this.start.y;
        this.start = null;
        if (Math.abs(dx) > 35 && Math.abs(dx) > Math.abs(dy)) move(dx < 0 ? 1 : -1);
      }, {signal});
      window.addEventListener('pointercancel', () => { this.start = null; }, {signal});
      this.gallery.querySelector('[id^="GalleryViewer"]').addEventListener('slideChanged', event => {
        const active = event.detail.currentElement;
        if (!active?.dataset.mediaId || active.parentElement !== this.list) return;
        this.list.querySelectorAll('[data-media-id]').forEach(item => {
          if (item.classList.contains('is-active') !== (item === active)) item.classList.toggle('is-active', item === active);
        });
      }, {signal});
    }
    disconnectedCallback() { this.controller?.abort(); }
  });

  if (!customElements.get('sp-product-set')) customElements.define('sp-product-set', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-pdp-set-variant]')) return;
        const option = event.target.selectedOptions[0], card = event.target.closest('.sp-pdp-set-card');
        card.querySelector('[data-pdp-set-price]').textContent = option.dataset.price;
        const compare = card.querySelector('[data-pdp-set-compare]');
        compare.textContent = option.dataset.compare || ''; compare.hidden = !option.dataset.compare;
        const input = card.querySelector('input[name=id]'); input.value = option.value;
        const button = card.querySelector('[name=add]'); button.disabled = option.disabled;
      }, {signal:this.controller.signal});
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
})();

/* Keep the horizontal gallery viewport as tall as its selected media only. */
(() => {
  const galleries = new Map();
  function boot() {
    for (const [gallery, dispose] of galleries) if (!gallery.isConnected) { dispose(); galleries.delete(gallery); }
    document.querySelectorAll('.sp-pdp media-gallery').forEach(gallery => {
      if (galleries.has(gallery)) return;
      const list=gallery.querySelector('.product__media-list');
      if(!list)return;
      let frame;
      const update=()=>{
        cancelAnimationFrame(frame);
        frame=requestAnimationFrame(()=>{
          const item=list.querySelector('.is-active')||list.querySelector('[data-media-id]');
          const media=item?.querySelector('.product-media-container')||item;
          const height=media?.getBoundingClientRect().height;
          if(!height)return;
          const value=height+'px';
          if(list.style.height!==value)list.style.height=value;
          const controls=gallery.querySelector('.sp-pdp-gallery-controls');
          if(controls && controls.style.height!==value)controls.style.height=value;
        });
      };
      const resize=new ResizeObserver(update);
      resize.observe(list);
      const observeMedia=()=>list.querySelectorAll('.product-media-container').forEach(e=>resize.observe(e));
      observeMedia();
      const mutation=new MutationObserver(()=>{observeMedia();update();});
      mutation.observe(list,{subtree:true,childList:true,attributes:true,attributeFilter:['class']});
      galleries.set(gallery,()=>{resize.disconnect();mutation.disconnect();cancelAnimationFrame(frame);});
      update();
    });
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
  new MutationObserver(boot).observe(document.documentElement,{childList:true,subtree:true});
})();
